import { useEffect } from "react";
import { useCommunity } from "context/community";
import { useLocation } from "react-router-dom";
import { PAGES } from "routes/PAGES";

const CHAT_SCRIPT_ID = "community-chat-assistant-script";
const CHAT_SCRIPT_NAME = "web-chat";
const CHAT_OPTION_TYPES = ["COMMUNITY_CHAT_ASSISTANT", "ENABLE_COMMUNITY_CHAT_ASSISTANT"];
const DEFAULT_SCRIPT_SRC = "https://portal.joinee.io/chat-scripts-MqGN74WP/web-chat.js";
const DEFAULT_CLIENT_SECRET = "e6f2a155-745a-4ff7-8985-5fcd0e370fcf";
const CHAT_ALLOWED_PATHS = [PAGES.WELCOME, PAGES.FAQ];
const CHAT_WIDGET_HOST_PART = "chat.newo.ai";

const safeParseJson = (value) => {
    if (typeof value !== "string") {
        return null;
    }
    try {
        return JSON.parse(value);
    } catch {
        return null;
    }
};

const normalizeOptions = (community) => {
    if (Array.isArray(community?.optionsRaw) && community.optionsRaw.length) {
        return community.optionsRaw;
    }
    if (Array.isArray(community?.options)) {
        return community.options.map((option) => {
            if (typeof option === "string") {
                return { optionType: option };
            }
            return option;
        });
    }
    return [];
};

const getAssistantConfig = (community) => {
    const options = normalizeOptions(community);
    const option = options.find((item) => CHAT_OPTION_TYPES.includes(item?.optionType));
    if (!option) {
        return null;
    }

    const parsedValue = safeParseJson(option.optionValue);
    const rawValue = typeof option.optionValue === "string" ? option.optionValue.trim() : "";

    const enabled = typeof parsedValue?.enabled === "boolean" ? parsedValue.enabled : true;
    if (!enabled) {
        return null;
    }

    const scriptSrc = parsedValue?.scriptSrc || DEFAULT_SCRIPT_SRC;
    const clientSecret = parsedValue?.clientSecret || (parsedValue ? "" : rawValue) || DEFAULT_CLIENT_SECRET;
    if (!clientSecret) {
        return null;
    }

    return { scriptSrc, clientSecret };
};

const removeChatScript = () => {
    const scriptNode = document.getElementById(CHAT_SCRIPT_ID);
    if (scriptNode) {
        scriptNode.remove();
    }
};

const removeChatWidgetArtifacts = () => {
    const selectors = [
        "web-chat",
        "iframe[src*='chat.newo.ai']",
        "iframe[src*='newo.ai']",
        "[id*='web-chat']",
        "[class*='web-chat']",
        "[id*='newo']",
        "[class*='newo']",
    ];

    selectors.forEach((selector) => {
        document.querySelectorAll(selector).forEach((node) => {
            node.remove();
        });
    });

    document.querySelectorAll("iframe").forEach((iframe) => {
        try {
            const iframeUrl = iframe.src ? new URL(iframe.src) : null;
            if (iframeUrl?.hostname?.includes(CHAT_WIDGET_HOST_PART)) {
                iframe.remove();
            }
        } catch {
            // Ignore malformed URLs from unrelated iframes.
        }
    });
};

const cleanupChatAssistant = () => {
    removeChatScript();
    removeChatWidgetArtifacts();
};

const upsertChatScript = ({ scriptSrc, clientSecret }) => {
    removeChatScript();

    const script = document.createElement("script");
    script.id = CHAT_SCRIPT_ID;
    script.src = scriptSrc;
    script.setAttribute("name", CHAT_SCRIPT_NAME);
    script.setAttribute("data-client-secret", clientSecret);
    script.async = true;
    document.body.appendChild(script);
};

const CommunityChatAssistant = () => {
    const { community } = useCommunity();
    const { pathname } = useLocation();

    useEffect(() => {
        const isAllowedPath = CHAT_ALLOWED_PATHS.includes(pathname);
        if (!isAllowedPath) {
            cleanupChatAssistant();
            return;
        }

        const config = getAssistantConfig(community);
        if (!config) {
            cleanupChatAssistant();
            return;
        }

        upsertChatScript(config);

        return () => {
            const currentPathAllowed = CHAT_ALLOWED_PATHS.includes(window.location.pathname);
            if (!currentPathAllowed) {
                cleanupChatAssistant();
            }
        };
    }, [community, pathname]);

    useEffect(() => {
        return () => {
            cleanupChatAssistant();
        };
    }, []);

    return null;
};

export default CommunityChatAssistant;
